// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Ball impacts — one recipe per surface (M3-24, 07 §3.3).
//
// A ball hitting a rubber post and a ball hitting a metal rail are not the same sound at
// different volumes; they are different sounds, and a machine that plays one clip for both is
// a machine where the player cannot tell by ear what the ball just did. 07 §3.3 gives six
// recipes and this file gives eight, for two reasons the table does not cover:
//
//   * **`flipperRubber` is its own material in the core and its own sound here.** A flipper is
//     a rubber sleeve over a nylon bat with a coil assembly bolted behind it, and it is deader
//     and lower than a rubber post on a wooden playfield. It is also, by a wide margin, the
//     contact a player hears most, so making it an alias of the post would be making the most
//     frequent sound in the game the one that is not quite right.
//   * **`glass` is missing from the table entirely.** The core has had a glass ceiling since
//     M1 because airballs are real, and a ball coming off a ramp into the underside of the
//     glass is one of the most recognisable noises a pinball machine makes. Leaving it out
//     would have made the loudest surprise in the game silent.
//
// **Every recipe is the same three ingredients** — a filtered noise transient, a set of modal
// partials, and optionally a pitch-dropping pluck — because that is what an impact physically
// is: the surfaces meeting (broadband, brief), the body ringing (modal, longer), and, for
// something elastic, a tensioned rebound that falls in pitch as it relaxes. The recipes differ
// in numbers, not in structure, which is what makes a ninth surface a row rather than a
// function.
//
// Lengths are deliberately short. An impact is a transient; the continuous part of a ball
// touching something is the rolling layer, and a wire that rings for a third of a second is
// eight buffers of a budget 07 §9 caps at forty milliseconds of render.

import {
  addNoiseBurst,
  addPartial,
  addPitchDrop,
  Biquad,
  fadeOut,
  filterInPlace,
  normalise,
  OnePole,
  samplesFor,
  type NoiseSource,
  type Samples,
} from './dsp'

/**
 * The surfaces, in `MATERIAL_NAMES` order, with ball-against-ball appended.
 *
 * The order is load-bearing: `IMPACT_CUES[surface]` is how a contact code from the solver
 * becomes a sound, and the core numbers its materials by their position in that list. A test
 * asserts the two agree, because the failure otherwise is a ball that sounds like wood when it
 * hits a rail — plausible enough that nobody would file it.
 */
export const IMPACT_CUES = [
  'impactRubber',
  'impactFlipperRubber',
  'impactMetal',
  'impactWood',
  'impactPlastic',
  'impactWire',
  'impactGlass',
  'impactBall',
] as const

export type ImpactCue = (typeof IMPACT_CUES)[number]

/** One partial of a struck body: a decaying sinusoid, which is what a struck body ring is. */
export interface ImpactMode {
  readonly hz: number
  readonly gain: number
  readonly decayMs: number
}

/** The elastic rebound of something under tension. A rubber pluck, and nothing else. */
export interface ImpactPluck {
  readonly fromHz: number
  readonly toHz: number
  readonly glideMs: number
  readonly decayMs: number
  readonly gain: number
}

export interface ImpactRecipe {
  /** The surfaces meeting: brief filtered noise. Centre, Q, length and level. */
  readonly noiseHz: number
  readonly noiseQ: number
  readonly noiseDecayMs: number
  readonly noiseGain: number
  /** The body ringing. Empty for a surface that does not ring at all. */
  readonly modes: readonly ImpactMode[]
  readonly pluck?: ImpactPluck
  /** A lowpass over the finished sound, for a surface that is soft or is behind something. */
  readonly muffleHz?: number
}

/**
 * The eight recipes.
 *
 * The four numbers 07 §3.3 states outright — metal's 4 kHz and 2.2 kHz, rubber's 300 Hz,
 * wood's 200 Hz, plastic's 1.6 kHz, wire's 3 kHz, the ball pair's 5 kHz and 3.4 kHz — are the
 * doc's. The decays, Qs and levels are what "bright tick", "dull knock", "clack" and "long
 * thin decay" mean once they have to be numbers.
 */
export const IMPACT_RECIPES: Readonly<Record<ImpactCue, ImpactRecipe>> = {
  // A rubber post. The pluck is the sound: rubber stretches under the ball and lets go, and
  // the pitch falls as the tension comes off. Lowpassed, because rubber has no top end.
  impactRubber: {
    noiseHz: 900,
    noiseQ: 1,
    noiseDecayMs: 4,
    noiseGain: 0.28,
    modes: [],
    pluck: { fromHz: 300, toHz: 140, glideMs: 18, decayMs: 40, gain: 1 },
    muffleHz: 1800,
  },
  // The bat. Lower and deader than a post — there is a coil assembly behind it absorbing the
  // hit, not a wooden playfield ringing along with it.
  impactFlipperRubber: {
    noiseHz: 700,
    noiseQ: 1,
    noiseDecayMs: 4,
    noiseGain: 0.22,
    modes: [],
    pluck: { fromHz: 220, toHz: 110, glideMs: 22, decayMs: 32, gain: 1 },
    muffleHz: 1200,
  },
  // A rail or a post cap. Bright tick plus the ping, exactly as the table has it.
  impactMetal: {
    noiseHz: 4000,
    noiseQ: 1.6,
    noiseDecayMs: 8,
    noiseGain: 1,
    modes: [{ hz: 2200, gain: 0.55, decayMs: 45 }],
  },
  // The playfield itself, and the one impact that is mostly noise. The single mode is the
  // panel answering underneath — a plywood playfield struck by a steel ball is not a dead
  // thud, and leaving it out gives a sound with no box around it.
  impactWood: {
    noiseHz: 200,
    noiseQ: 1.2,
    noiseDecayMs: 30,
    noiseGain: 1,
    modes: [{ hz: 340, gain: 0.3, decayMs: 26 }],
    muffleHz: 2500,
  },
  // A plastic ramp wall or a post sleeve. Short, mid, and hard-edged.
  impactPlastic: {
    noiseHz: 1600,
    noiseQ: 2,
    noiseDecayMs: 15,
    noiseGain: 1,
    modes: [{ hz: 2600, gain: 0.25, decayMs: 18 }],
  },
  // A habitrail. Two thin partials and almost no noise, which is what "thin" means: a wire has
  // very little mass to make a broadband noise with, and a very long time to ring for.
  impactWire: {
    noiseHz: 3000,
    noiseQ: 4,
    noiseDecayMs: 3,
    noiseGain: 0.35,
    modes: [
      { hz: 3000, gain: 1, decayMs: 180 },
      { hz: 4310, gain: 0.3, decayMs: 120 },
    ],
  },
  // The underside of the glass. Bright and hard, but damped rather than ringing: the sheet is
  // clamped in a channel all the way round, which is why a pinball glass goes *tok* and a pane
  // in a window frame does not.
  impactGlass: {
    noiseHz: 3200,
    noiseQ: 1.2,
    noiseDecayMs: 7,
    noiseGain: 1,
    modes: [{ hz: 1900, gain: 0.35, decayMs: 55 }],
  },
  // Ball against ball. Two tones because two identical spheres ring identically, and hearing
  // the beat between them is exactly how a player knows, without looking, that they still have
  // more than one ball.
  impactBall: {
    noiseHz: 5000,
    noiseQ: 3,
    noiseDecayMs: 2,
    noiseGain: 0.4,
    modes: [
      { hz: 5000, gain: 1, decayMs: 6 },
      { hz: 3400, gain: 0.8, decayMs: 9 },
    ],
  },
}

/** How long a recipe needs before it is inaudible. Same 1.15× tail as the coils. */
export function impactLengthMs(recipe: ImpactRecipe): number {
  let longest = recipe.noiseDecayMs
  for (const mode of recipe.modes) if (mode.decayMs > longest) longest = mode.decayMs
  if (recipe.pluck !== undefined && recipe.pluck.decayMs > longest) longest = recipe.pluck.decayMs
  return longest * 1.15 + 8
}

/**
 * Render one impact.
 *
 * `pitchScale` moves the whole recipe, which is right here for the reason it is wrong for a
 * chime: an impact has no tuning to lose, and a ball rattling between two posts fires the same
 * buffer four times in three hundred milliseconds if it does not vary.
 *
 * Normalised, so the recipe decides the character and the mix decides the level.
 */
export function renderImpact(
  recipe: ImpactRecipe,
  sampleRate: number,
  noise: NoiseSource,
  pitchScale = 1,
): Samples {
  const out = new Float32Array(samplesFor(impactLengthMs(recipe), sampleRate))

  if (recipe.pluck !== undefined) {
    const p = recipe.pluck
    addPitchDrop(
      out,
      sampleRate,
      p.fromHz * pitchScale,
      p.toHz * pitchScale,
      p.glideMs,
      p.gain,
      p.decayMs,
    )
  }

  for (const mode of recipe.modes) {
    addPartial(out, sampleRate, mode.hz * pitchScale, mode.gain, mode.decayMs)
  }

  addNoiseBurst(
    out,
    sampleRate,
    noise,
    Biquad.bandpass(recipe.noiseHz * pitchScale, recipe.noiseQ, sampleRate),
    recipe.noiseGain,
    recipe.noiseDecayMs,
  )

  if (recipe.muffleHz !== undefined) {
    filterInPlace(out, new OnePole(recipe.muffleHz, sampleRate))
  }

  normalise(out)
  fadeOut(out, sampleRate)
  return out
}
